import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Trash2, ShoppingBag, ArrowRight, Info } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const devErr = process.env.NODE_ENV !== 'production' ? console.error.bind(console) : () => {};
const fmt = (p) => `R${Number(p).toLocaleString('en-ZA', { minimumFractionDigits: 0 })}`;

const DISCLAIMER = "Prices shown do not include shipping costs. Items are sourced from reputable boutiques globally. Shipping fees are calculated after sourcing and communicated to you.";

const STATUS_LABELS = {
  pending: { label: 'In Bag', color: 'text-[#555555] bg-[#F3F3F3]' },
  requested: { label: 'Requested', color: 'text-[#D4AF37] bg-[#D4AF37]/10' },
  confirmed: { label: 'Confirmed', color: 'text-[#2E7D32] bg-[#2E7D32]/10' },
  payment_sent: { label: 'Payment Ready', color: 'text-[#0A0A0A] bg-[#D4AF37]/20' },
};

export default function Bag() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requestingId, setRequestingId] = useState(null);
  const [requestingAll, setRequestingAll] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/auth'); return; }
    fetchBag();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  const fetchBag = async () => {
    try {
      const { data } = await axios.get(`${API}/bag`, { withCredentials: true });
      setItems(data);
    } catch {
      // silently handle bag fetch error
      devErr('[Bag] fetchBag failed');
    } finally {
      setLoading(false);
    }
  };

  const removeItem = async (bagItemId) => {
    await axios.delete(`${API}/bag/${bagItemId}`, { withCredentials: true });
    setItems(items.filter(i => i.bag_item_id !== bagItemId));
  };

  const requestPurchase = async (bagItemId) => {
    setRequestingId(bagItemId);
    try {
      await axios.post(`${API}/bag/${bagItemId}/request`, {}, { withCredentials: true });
      setItems(items.map(i => i.bag_item_id === bagItemId ? { ...i, status: 'requested' } : i));
    } catch {
      // silently handle request error
      devErr('[Bag] requestPurchase failed');
    } finally {
      setRequestingId(null);
    }
  };

  const requestAll = async () => {
    setRequestingAll(true);
    for (const item of pendingItems) {
      await requestPurchase(item.bag_item_id);
    }
    setRequestingAll(false);
  };

  const pendingItems = items.filter(i => i.status === 'pending');
  const total = pendingItems.reduce((sum, i) => sum + (i.product?.final_price || 0), 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-white pt-20 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen pt-16 lg:pt-20 pb-28 lg:pb-0" data-testid="bag-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12 py-8 lg:py-12">
        <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-2 font-work">Personal Shopper</p>
        <h1 className="font-playfair text-3xl lg:text-4xl text-[#0A0A0A] mb-8 lg:mb-12">My Bag</h1>

        {items.length === 0 ? (
          <div className="text-center py-20">
            <ShoppingBag size={48} className="mx-auto text-[#E5E5E5] mb-6" />
            <p className="font-playfair text-2xl text-[#0A0A0A] mb-4">Your bag is empty</p>
            <p className="text-[#999999] text-sm font-work mb-8">Discover luxury pieces and add them to your bag</p>
            <Link to="/shop">
              <button className="bg-[#0A0A0A] text-white hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-colors px-10 py-4 text-xs uppercase tracking-widest font-work">
                Shop Now
              </button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
            {/* Items */}
            <div className="lg:col-span-2 flex flex-col gap-5">
              {items.map(item => {
                const p = item.product;
                const st = STATUS_LABELS[item.status] || STATUS_LABELS.pending;
                return (
                  <div key={item.bag_item_id} className="flex gap-4 border-b border-[#F3F3F3] pb-5" data-testid={`bag-item-${item.bag_item_id}`}>
                    <div
                      className="w-20 h-24 sm:w-24 sm:h-28 flex-shrink-0 bg-[#F9F9F9] overflow-hidden cursor-pointer"
                      onClick={() => navigate(`/product/${p.product_id}`)}
                    >
                      <img src={p.images?.[0]} alt={p.name} className="object-cover w-full h-full" />
                    </div>
                    <div className="flex-1 flex flex-col gap-1.5 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold font-work">{p.designer}</p>
                          <p className="text-sm font-medium text-[#0A0A0A] font-work leading-tight pr-2">{p.name}</p>
                          {item.size && <p className="text-xs text-[#999999] font-work mt-0.5">Size: {item.size}</p>}
                        </div>
                        <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 font-work whitespace-nowrap flex-shrink-0 ${st.color}`}>{st.label}</span>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-[#AAAAAA] line-through text-xs font-work">{fmt(p.original_price)}</p>
                        <p className="text-[#0A0A0A] font-semibold text-sm font-work">{fmt(p.final_price)}</p>
                        <p className="text-[#D4AF37] text-xs font-work">Save {fmt(p.savings)}</p>
                      </div>

                      <div className="flex items-center gap-3 mt-1">
                        {item.status === 'pending' && (
                          <button
                            onClick={() => requestPurchase(item.bag_item_id)}
                            disabled={requestingId === item.bag_item_id}
                            className="text-xs uppercase tracking-wider font-work text-[#0A0A0A] hover:text-[#D4AF37] transition-colors border-b border-[#0A0A0A] hover:border-[#D4AF37] pb-0.5 disabled:opacity-50"
                            data-testid={`request-btn-${item.bag_item_id}`}
                          >
                            {requestingId === item.bag_item_id ? 'Requesting...' : 'Request Purchase'}
                          </button>
                        )}
                        {item.status === 'requested' && (
                          <p className="text-xs text-[#D4AF37] font-work">Personal shopper will contact you shortly</p>
                        )}
                        {item.status === 'pending' && (
                          <button
                            onClick={() => removeItem(item.bag_item_id)}
                            className="text-[#CCCCCC] hover:text-[#D32F2F] transition-colors ml-auto p-1"
                            data-testid={`remove-btn-${item.bag_item_id}`}
                            aria-label="Remove item"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop Order Summary */}
            <div className="hidden lg:block lg:col-span-1">
              <div className="bg-[#F9F9F9] p-6 sticky top-24">
                <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0A0A0A] mb-6 font-work">Order Summary</p>
                <div className="flex flex-col gap-3 mb-6">
                  <div className="flex justify-between text-sm font-work">
                    <span className="text-[#555555]">Items ({pendingItems.length})</span>
                    <span className="text-[#0A0A0A] font-semibold">{fmt(total)}</span>
                  </div>
                  <div className="flex justify-between text-sm font-work">
                    <span className="text-[#555555]">Shipping</span>
                    <span className="text-[#D4AF37] font-work text-xs">Calculated after sourcing</span>
                  </div>
                </div>
                <div className="border-t border-[#E5E5E5] pt-4 mb-6">
                  <div className="flex justify-between font-work">
                    <span className="text-sm font-semibold text-[#0A0A0A]">Subtotal</span>
                    <span className="text-lg font-bold text-[#0A0A0A]">{fmt(total)}</span>
                  </div>
                </div>
                {pendingItems.length > 0 && (
                  <button
                    onClick={requestAll}
                    disabled={requestingAll}
                    className="w-full bg-[#0A0A0A] text-white hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-colors py-4 text-xs uppercase tracking-widest font-semibold font-work flex items-center justify-center gap-2 disabled:opacity-60"
                    data-testid="request-all-btn"
                  >
                    {requestingAll ? 'Requesting...' : <><span>Request All</span> <ArrowRight size={14} /></>}
                  </button>
                )}
                <div className="mt-4 flex items-start gap-2">
                  <Info size={12} className="text-[#D4AF37] mt-0.5 flex-shrink-0" />
                  <p className="text-[10px] text-[#AAAAAA] font-work leading-relaxed">{DISCLAIMER}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Mobile sticky checkout bar */}
      {pendingItems.length > 0 && (
        <div className="lg:hidden fixed bottom-16 left-0 right-0 z-40 bg-white border-t border-[#E5E5E5] px-4 py-3 shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-[#555555] font-work">{pendingItems.length} item{pendingItems.length > 1 ? 's' : ''}</span>
            <span className="text-sm font-bold text-[#0A0A0A] font-work">{fmt(total)}</span>
          </div>
          <button
            onClick={requestAll}
            disabled={requestingAll}
            className="w-full bg-[#0A0A0A] text-white py-3.5 text-xs uppercase tracking-widest font-semibold font-work flex items-center justify-center gap-2 active:bg-[#D4AF37] active:text-[#0A0A0A] transition-colors disabled:opacity-60"
            data-testid="mobile-request-all-btn"
          >
            {requestingAll ? 'Requesting...' : <><span>Request All Items</span> <ArrowRight size={14} /></>}
          </button>
        </div>
      )}
    </div>
  );
}
